import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import CrudManager from "@/pages/admin/CrudManager";
import SettingsForm from "@/pages/admin/SettingsForm";
import RequestsView from "@/pages/admin/RequestsView";
import AnalyticsView from "@/pages/admin/AnalyticsView";
import QRView from "@/pages/admin/QRView";
import MediaLibrary from "@/pages/admin/MediaLibrary";
import OrderManager from "@/pages/admin/OrderManager";
import GalleryOrderManager from "@/pages/admin/GalleryOrderManager";
import { CATEGORIES, SPECIALTY_EXCLUDE_IDS, GALLERY_LABELS, galleryCategory } from "@/lib/api";
import { UtensilsCrossed, CalendarDays, Sparkles, Clock, Users2, Image, BarChart3, QrCode, Settings, LogOut, Home, Star, ArrowUpDown } from "lucide-react";

const catOptions = CATEGORIES.map((c) => ({ value: c.key, label: c.label }));

const dishFields = [
  { key: "nome", label: "Nome del piatto", type: "text" },
  { key: "categoria", label: "Categoria", type: "select", options: catOptions },
  { key: "descrizione", label: "Descrizione (opzionale)", type: "textarea", ai: true },
  { key: "prezzo", label: "Prezzo (es. 13,00)", type: "text" },
  { key: "allergeni", label: "Allergeni (opzionale)", type: "text" },
  { key: "image", label: "Fotografia (opzionale)", type: "image" },
  { key: "image_alt", label: "Testo alternativo foto (ALT)", type: "text" },
  { key: "specialita", label: "Specialità Masetti", type: "switch" },
  { key: "fatto_in_casa", label: "Fatto in casa", type: "switch" },
  { key: "stagionale", label: "Stagionale", type: "switch" },
  { key: "home_evidenza", label: "In evidenza nel carosello della home (telefono)", type: "switch" },
  { key: "available", label: "Disponibile (mostra nel menù)", type: "switch", default: true },
];
const eventFields = [
  { key: "titolo", label: "Titolo", type: "text" },
  { key: "date", label: "Data (es. Sab 12 Ottobre)", type: "text" },
  { key: "descrizione", label: "Descrizione", type: "textarea" },
  { key: "prezzo", label: "Prezzo (opzionale)", type: "text" },
  { key: "posti", label: "Posti disponibili (opzionale)", type: "number" },
  { key: "image", label: "Immagine", type: "image" },
  { key: "image_alt", label: "Testo alternativo foto (ALT)", type: "text" },
  { key: "sold_out", label: "Sold Out", type: "switch" },
  { key: "published", label: "Pubblicato", type: "switch", default: true },
];
const specialFields = [
  { key: "titolo", label: "Titolo (es. Sono tornati i porcini)", type: "text" },
  { key: "testo", label: "Breve testo", type: "textarea" },
  { key: "image", label: "Foto / video", type: "image", accept: "image/*,video/*" },
  { key: "image_alt", label: "Testo alternativo foto (ALT)", type: "text" },
  { key: "published", label: "Pubblicato", type: "switch", default: true },
];
const historyFields = [
  { key: "year", label: "Anno o periodo (opzionale)", type: "text" },
  { key: "title", label: "Titolo (opzionale)", type: "text" },
  { key: "desc", label: "Descrizione / didascalia", type: "textarea" },
  { key: "persone", label: "Persone presenti (opzionale)", type: "text" },
  { key: "image", label: "Fotografia storica", type: "image" },
  { key: "image_alt", label: "Testo alternativo foto (ALT)", type: "text" },
  { key: "order", label: "Ordine di visualizzazione", type: "number" },
];
const storyFields = [
  { key: "nome", label: "Nome della persona (opzionale)", type: "text" },
  { key: "anno", label: "Anno (opzionale)", type: "text" },
  { key: "persone", label: "Persone presenti (opzionale)", type: "text" },
  { key: "didascalia", label: "Breve storia / didascalia", type: "textarea" },
  { key: "image", label: "Fotografia", type: "image" },
  { key: "image_alt", label: "Testo alternativo foto (ALT)", type: "text" },
  { key: "show_nome", label: "Mostra nome", type: "switch", default: true },
  { key: "show_anno", label: "Mostra anno", type: "switch", default: true },
  { key: "order", label: "Ordine di visualizzazione", type: "number" },
];
const galleryFields = [
  { key: "category", label: "Sezione", type: "select", options: [
    { value: "storiche", label: "Foto storiche" }, { value: "ambienti", label: "Gli ambienti (sala, terrazza, spazi allestiti)" },
    { value: "cucina", label: "La nostra cucina (preparazioni, pasta fresca, griglia)" },
  ] },
  { key: "title", label: "Titolo (opzionale)", type: "text" },
  { key: "caption", label: "Didascalia (opzionale)", type: "text" },
  { key: "image", label: "Fotografia o video (mp4/webm)", type: "image", accept: "image/*,video/mp4,video/webm,video/quicktime" },
  { key: "image_alt", label: "Testo alternativo foto (ALT)", type: "text" },
  { key: "active", label: "Visibile sul sito", type: "switch", default: true },
  { key: "order", label: "Ordine di visualizzazione", type: "number" },
];
const testimonialFields = [
  { key: "autore", label: "Nome del cliente", type: "text" },
  { key: "testo", label: "Testo della recensione", type: "textarea" },
  { key: "rating", label: "Stelle", type: "select", options: [
    { value: "5", label: "★★★★★ (5)" }, { value: "4", label: "★★★★ (4)" }, { value: "3", label: "★★★ (3)" },
  ] },
  { key: "fonte", label: "Fonte (es. Google)", type: "text" },
  { key: "published", label: "Pubblicata", type: "switch", default: true },
  { key: "order", label: "Ordine di visualizzazione", type: "number" },
];

const TABS = [
  { key: "dishes", label: "Menù", icon: UtensilsCrossed },
  { key: "ordine", label: "Ordine in evidenza", icon: ArrowUpDown },
  { key: "events", label: "Eventi", icon: CalendarDays },
  { key: "specials", label: "Oggi da Masetti", icon: Sparkles },
  { key: "testimonials", label: "Recensioni", icon: Star },
  { key: "history", label: "Storia", icon: Clock },
  { key: "stories", label: "Persone e storie", icon: Users2 },
  { key: "gallery", label: "Galleria", icon: Image },
  { key: "media", label: "Libreria immagini", icon: Image },
  { key: "requests", label: "Richieste", icon: Users2 },
  { key: "analytics", label: "Analytics", icon: BarChart3 },
  { key: "qr", label: "QR Code", icon: QrCode },
  { key: "settings", label: "Impostazioni", icon: Settings },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("dishes");

  return (
    <div className="min-h-screen bg-cream-dark">
      <header className="sticky top-0 z-40 bg-oak-dark text-cream">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div>
            <p className="font-serif text-lg text-brass leading-none">Masetti · Amministrazione</p>
            <p className="text-[11px] text-cream/50">{user.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/"><Button size="sm" variant="ghost" className="text-cream hover:bg-cream/10" data-testid="admin-view-site"><Home className="w-4 h-4 mr-1" />Sito</Button></Link>
            <Button size="sm" variant="ghost" onClick={logout} className="text-cream hover:bg-cream/10" data-testid="admin-logout"><LogOut className="w-4 h-4 mr-1" />Esci</Button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-2 overflow-x-auto no-scrollbar border-t border-cream/10">
          <div className="flex gap-1 py-1 min-w-max">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm whitespace-nowrap ${tab === t.key ? "bg-brass text-oak-dark" : "text-cream/70 hover:bg-cream/10"}`}>
                  <Icon className="w-4 h-4" />{t.label}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {tab === "dishes" && <CrudManager title="Piatti del menù" endpoint="dishes" listParams="?all=true" fields={dishFields} />}
        {tab === "ordine" && (
          <div className="space-y-6">
            <div className="mb-1">
              <h2 className="font-serif text-2xl text-bordeaux">Ordine delle sezioni in evidenza</h2>
              <p className="text-sm text-oak/60 mt-1">Trascina i piatti (o usa le frecce) per cambiare l'ordine con cui compaiono nella panoramica del menù, poi premi <strong>Salva ordine</strong>.</p>
            </div>
            <OrderManager title="Le specialità Masetti" subtitle="L'ordine con cui appaiono nella sezione «Le specialità Masetti»." flag="specialita" field="specialita_order" excludeIds={SPECIALTY_EXCLUDE_IDS} />
            <OrderManager title="Profumi di stagione" subtitle="L'ordine con cui appaiono nella sezione «Profumi di stagione»." flag="stagionale" field="stagione_order" />
          </div>
        )}
        {tab === "events" && <CrudManager title="Eventi" endpoint="events" listParams="?all=true" fields={eventFields} />}
        {tab === "specials" && <CrudManager title="Oggi da Masetti" endpoint="specials" listParams="?all=true" fields={specialFields} />}
        {tab === "testimonials" && <CrudManager title="Recensioni in evidenza" endpoint="testimonials" listParams="?all=true" fields={testimonialFields} renderTitle={(it) => `${"★".repeat(Number(it.rating) || 5)} — ${it.autore || ""}`} />}
        {tab === "history" && <CrudManager title="Timeline della storia" endpoint="history" fields={historyFields} />}
        {tab === "stories" && <CrudManager title="Persone e storie passate da Masetti" endpoint="stories" fields={storyFields} renderTitle={(it) => it.nome || it.didascalia || "Foto d'archivio"} />}
        {tab === "gallery" && (
          <div className="space-y-8">
            <CrudManager title="Galleria fotografica" endpoint="gallery" listParams="?all=true" fields={galleryFields} renderTitle={(it) => `${GALLERY_LABELS[galleryCategory(it.category)] || it.category || ""} — ${it.title || it.caption || "foto"}`} />
            <GalleryOrderManager />
          </div>
        )}
        {tab === "media" && <MediaLibrary />}
        {tab === "requests" && <RequestsView />}
        {tab === "analytics" && <AnalyticsView />}
        {tab === "qr" && <QRView />}
        {tab === "settings" && <SettingsForm />}
      </div>
    </div>
  );
}
